import { AwardVariant, ProductAxis, VariantType } from '~/utils/types';
import { valueImage } from '~/utils/productGroup';
import type { VariantSelection } from '~/hooks/useVariantSelection';
import styles from './variant-selector.module.scss';

export const DB_VARIANT_LABEL: Record<VariantType, string> = {
  [VariantType.COLOR]: 'Color',
  [VariantType.SIZE]: 'Talla',
  [VariantType.GENERAL]: 'Tipo',
};

/** Nombre del grupo de DbVariant: «Talla», «Color» u «Opciones» si se mezclan. */
export function dbVariantGroupLabel(variants: AwardVariant[]): string {
  const types = Array.from(new Set(variants.map((v) => v.type)));
  return types.length === 1 ? (DB_VARIANT_LABEL[types[0]] ?? 'Opciones') : 'Opciones';
}

const STATE_CLASS = {
  selected: styles.isSelected,
  available: '',
  conflict: styles.isConflict,
  disabled: styles.isDisabled,
};

const STATE_HINT = {
  selected: '',
  available: '',
  conflict: ' (no disponible con lo elegido)',
  disabled: ' (no disponible)',
};

// Un eje del producto según su modo de presentación: pills (TEXT), swatches
// (COLOR) o miniaturas (IMAGE). El nombre del valor elegido va junto al eje.
function AxisPicker({ axis, selection }: { axis: ProductAxis; selection: VariantSelection }) {
  const { group } = selection;
  const chosenKey = selection.selection[axis.key];
  const chosen = axis.values.find((v) => v.key === chosenKey);

  return (
    <div className={styles.axis}>
      <div className={styles.axisHead} id={`axis-${axis.key}`}>
        <span className={styles.axisName}>{axis.name}</span>
        {chosen && <span className={styles.axisValue}>{chosen.name}</span>}
      </div>

      <div
        className={`${styles.values} ${axis.display === 'IMAGE' ? styles.valuesImage : ''}`}
        role="radiogroup"
        aria-labelledby={`axis-${axis.key}`}
      >
        {axis.values.map((value) => {
          const state = selection.stateOf(axis.key, value.key);
          const common = {
            type: 'button' as const,
            role: 'radio',
            'aria-checked': state === 'selected',
            'aria-disabled': state === 'disabled',
            disabled: state === 'disabled',
            title: value.name + STATE_HINT[state],
            onClick: () => selection.select(axis.key, value.key),
          };

          if (axis.display === 'COLOR' && value.swatch) {
            return (
              <button
                key={value.key}
                {...common}
                aria-label={value.name + STATE_HINT[state]}
                className={`${styles.swatch} ${STATE_CLASS[state]}`}
              >
                <span className={styles.swatchFill} style={{ background: value.swatch }} />
              </button>
            );
          }

          if (axis.display === 'IMAGE' && group) {
            const img = valueImage(group, axis.key, value);
            return (
              <button
                key={value.key}
                {...common}
                className={`${styles.thumb} ${STATE_CLASS[state]}`}
              >
                <span className={styles.thumbImg}>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  {img ? <img src={img} alt="" loading="lazy" /> : null}
                </span>
                <span className={styles.thumbName}>{value.name}</span>
              </button>
            );
          }

          return (
            <button key={value.key} {...common} className={`${styles.pill} ${STATE_CLASS[state]}`}>
              {value.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// DbVariant (talla/color legacy) como pills: solo en un premio sin `group`.
function DbVariantPicker({ selection }: { selection: VariantSelection }) {
  const { dbVariants, dbVariant } = selection;
  const label = dbVariantGroupLabel(dbVariants);

  return (
    <div className={styles.axis}>
      <div className={styles.axisHead} id="axis-db-variant">
        <span className={styles.axisName}>{label}</span>
        {dbVariant && <span className={styles.axisValue}>{dbVariant.name}</span>}
      </div>
      <div className={styles.values} role="radiogroup" aria-labelledby="axis-db-variant">
        {dbVariants.map((v) => {
          const selected = dbVariant?.id === v.id;
          return (
            <button
              key={v.id}
              type="button"
              role="radio"
              aria-checked={selected}
              className={`${styles.pill} ${selected ? styles.isSelected : ''}`}
              onClick={() => selection.setDbVariant(v)}
            >
              {v.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function VariantSelector({ selection }: { selection: VariantSelection }) {
  // Uno u otro, nunca los dos: con `group` solo los ejes del producto.
  const showAxes = selection.group != null;
  const showDb = !showAxes && selection.dbVariants.length > 0;
  if (!showAxes && !showDb) return null;

  return (
    <div className={styles.selector}>
      {showAxes &&
        selection.axes.map((axis) => (
          <AxisPicker key={axis.key} axis={axis} selection={selection} />
        ))}
      {showDb && <DbVariantPicker selection={selection} />}
    </div>
  );
}
